import { useMutation, useQuery } from '@apollo/client'
import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  Button,
  ButtonColor,
  Loader,
  LoaderType,
  PageSizeSelector,
} from '../../../components'
import { GET_BRAND_INVOICES, GET_CUSTOMER_PORTAL_SESSION } from '../../../gql'
import {
  Brand,
  MutationGetCustomerPortalSessionArgs,
  User,
} from '../../../gql/types'
import { BrandRoutes } from '../../BrandRoutes'
import { ClipCredits } from '../components/ClipCredits'
import { Header } from '../components/Header'
import { InvoicesList } from './InvoicesList'

// Updated interface to match our new backend response
interface GetInvoicesResponse {
  invoices: any[]
  hasMore: boolean
  nextCursor?: string
  currentPageSize: number
}

interface IProps {
  user: User
  brand: Brand
}

export const Billing: FunctionComponent<IProps> = ({ brand }) => {
  const [limit, setLimit] = useState(50) // Make limit configurable, default to 50
  const [startingAfter, setStartingAfter] = useState<string | undefined>(
    undefined,
  )
  const [isFirstPage, setIsFirstPage] = useState(true)
  const [displayedInvoices, setDisplayedInvoices] = useState<any[]>([]) // Keep invoices visible during loading
  const [hasInitialData, setHasInitialData] = useState(false) // Track if we ever loaded data
  const [displayedHasMore, setDisplayedHasMore] = useState(false) // Keep hasMore persistent
  const [displayedNextCursor, setDisplayedNextCursor] = useState<
    string | undefined
  >() // Keep nextCursor persistent

  const [getCustomerPortalSession, { loading, error }] = useMutation<
    any,
    MutationGetCustomerPortalSessionArgs
  >(GET_CUSTOMER_PORTAL_SESSION)

  const {
    loading: getInvoicesByBrandIdLoading,
    error: getInvoicesByBrandIdError,
    data: getInvoicesByBrandIdData,
  } = useQuery<
    { getInvoicesByBrandId: GetInvoicesResponse },
    { brandId: Number; options: { startingAfter?: string; limit: number } }
  >(GET_BRAND_INVOICES, {
    variables: {
      brandId: brand.id,
      options: {
        startingAfter,
        limit,
      },
    },
  })

  const redirectToCustomerPortal = async () => {
    try {
      const returnUrl = BrandRoutes.billing.replace(':id', `${brand.id}`)

      const {
        data: { getCustomerPortalSession: url },
      } = await getCustomerPortalSession({
        variables: {
          brandId: brand.id,
          input: {
            returnUrl,
          },
        },
      })

      window.open(url, '_blank', 'noreferrer')
    } catch (error) {
      console.error(error)
    }
  }

  const invoices =
    getInvoicesByBrandIdData?.getInvoicesByBrandId?.invoices || []
  const hasMore = getInvoicesByBrandIdData?.getInvoicesByBrandId?.hasMore
  const nextCursor = getInvoicesByBrandIdData?.getInvoicesByBrandId?.nextCursor

  // Update displayed data only when not loading
  useEffect(() => {
    if (!getInvoicesByBrandIdLoading) {
      setDisplayedInvoices(invoices)
      setDisplayedHasMore(hasMore || false)
      setDisplayedNextCursor(nextCursor)
      setHasInitialData(true) // Mark that we have loaded data at least once
    }
  }, [getInvoicesByBrandIdLoading, invoices, hasMore, nextCursor])

  const loadNextPage = () => {
    if (displayedHasMore && displayedNextCursor) {
      setStartingAfter(displayedNextCursor)
      setIsFirstPage(false)
    }
  }

  const loadFirstPage = () => {
    setStartingAfter(undefined)
    setIsFirstPage(true)
  }

  return (
    <div>
      <Header />

      <div className='flex flex-col space-y-2 max-w-md px-1'>
        {!brand && <Loader />}
        {brand && <ClipCredits brand={brand} />}

        <Button
          title='Update Payment Method'
          type='button'
          colour={ButtonColor.primary}
          loading={loading}
          disabled={loading}
          onClick={() => redirectToCustomerPortal()}
        />
        <p className='text-sm text-gray-500'>
          If you would like to split your credits between multiple brands,
          please email us at{' '}
          <a href='mailto:hello@useclip.com'>hello@useclip.com</a>.
        </p>
      </div>

      {/* Show real errors (but not "payment setup" - treat that as no invoices) */}
      {(error ||
        (getInvoicesByBrandIdError &&
          !getInvoicesByBrandIdError?.message?.includes('payment setup'))) && (
        <p className='mt-2'>There has been an error. Try refreshing the page</p>
      )}
      {/* Fixed header - always visible */}
      <div className='mt-4 px-1 flex justify-between items-center'>
        <p className='text-base font-semibold leading-6 text-gray-900'>
          Billing history
        </p>
        {(displayedInvoices?.length > 0 ||
          (getInvoicesByBrandIdLoading && hasInitialData)) &&
          !getInvoicesByBrandIdError?.message?.includes('payment setup') && (
            <PageSizeSelector
              value={limit}
              onChange={(newLimit) => {
                setLimit(newLimit)
                // Reset pagination when changing limit
                setStartingAfter(undefined)
                setIsFirstPage(true)
              }}
              disabled={getInvoicesByBrandIdLoading}
            />
          )}
      </div>

      {/* Content area - table or initial loading */}
      <div className='flow-root'>
        {getInvoicesByBrandIdError?.message?.includes('payment setup') && (
          <div className='mt-4 ml-1 text-left text-sm text-gray-500'>
            <p>No invoices found.</p>
          </div>
        )}
        {!getInvoicesByBrandIdError?.message?.includes('payment setup') && (
          <>
            {/* Show initial loader for first load */}
            {!hasInitialData && getInvoicesByBrandIdLoading && (
              <div className='mt-4 flex justify-center'>
                <Loader type={LoaderType.button} />
              </div>
            )}
            {/* Show table once we have initial data or not loading for first time */}
            {(hasInitialData ||
              (!getInvoicesByBrandIdLoading &&
                displayedInvoices.length === 0)) && (
              <div className='overflow-x-auto'>
                <div className='inline-block min-w-full py-2 align-middle px-1'>
                  <div className='overflow-hidden shadow md:w-auto ring-1 ring-black ring-opacity-5 rounded-t'>
                    <InvoicesList
                      invoices={displayedInvoices}
                      hasMore={displayedHasMore}
                      isFirstPage={isFirstPage}
                      onLoadMore={loadNextPage}
                      onLoadFirst={loadFirstPage}
                      loading={getInvoicesByBrandIdLoading}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
